import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import {
  FilterBox,
  InputRow,
  InputWrap,
  TextInput,
  ClearBtn,
  Segmented,
  SegBtn,
} from "./styles";

const Container = styled.svg`
  position: absolute;
  left: 8px;
  width: 16px;
  height: 16px;
  fill: #94a3b8;
  pointer-events: none;
`;

const SearchIcon = () => {
  return (
    <Container viewBox="0 0 24 24">
      <path d="M15.5 14h-.79l-.28-.27A6.5 6.5 0 1 0 14 15.5l.27.28v.79l5 5 1.5-1.5-5-5zM10 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" />
    </Container>
  );
};

const StringFilter = ({
  anchorRect,
  columnName,
  filter,
  setFilter,
  sortKey,
  sortAsc,
  onSortAsc,
  onSortDesc,
  onClose,
}) => {
  const [pos, setPos] = useState(null);
  const [arrowX, setArrowX] = useState(14);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const isAscActive = sortKey === columnName && sortAsc === true;
  const isDescActive = sortKey === columnName && sortAsc === false;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = 8;
    const gapY = 6;
    const minW = 220;
    const idealW = 300;
    const width = Math.min(idealW, Math.max(minW, vw - 2 * m));
    const approxH = 80;

    let left = anchorRect.left;
    let top = anchorRect.bottom + gapY;

    const overflowRight = left + width - (vw - m);
    if (overflowRight > 0) left = Math.max(m, left - overflowRight);
    left = Math.max(m, left);
    if (top + approxH > vh - m) top = Math.max(m, vh - m - approxH);

    setPos({ top, left, width });

    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const rel = anchorCenter - left;
    const clamped = Math.max(12, Math.min(rel, width - 12));
    setArrowX(clamped);
  }, [anchorRect]);

  useLayoutEffect(() => {
    if (!boxRef.current || !pos) return;
    const r = boxRef.current.getBoundingClientRect();
    const m = 8;
    let { top, left, width } = pos;
    let changed = false;

    if (r.right > window.innerWidth - m) {
      left = Math.max(m, window.innerWidth - m - width);
      changed = true;
    }
    if (r.left < m) {
      left = m;
      changed = true;
    }
    if (r.bottom > window.innerHeight - m) {
      top = Math.max(m, window.innerHeight - m - r.height);
      changed = true;
    }

    if (changed) {
      setPos({ top, left, width });
      const anchorCenter = anchorRect.left + anchorRect.width / 2;
      const rel = anchorCenter - left;
      const clamped = Math.max(12, Math.min(rel, width - 12));
      setArrowX(clamped);
    }
  }, [pos, anchorRect]);

  const Clear = () => {
    setFilter("");
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") onClose();
    if (e.key === "Escape") onClose();
  };

  const node = (
    <FilterBox
      ref={boxRef}
      style={
        pos
          ? {
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 9999,
              ["--arrow-x" as any]: `${arrowX}px`,
            }
          : { position: "fixed", top: -9999 }
      }
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <InputRow>
        <InputWrap>
          <SearchIcon />
          <TextInput
            ref={inputRef}
            value={filter}
            placeholder="필터를 입력해주세요."
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={onKeyDown}
          />
          {filter && <ClearBtn onClick={Clear}>×</ClearBtn>}
        </InputWrap>

        <Segmented>
          <SegBtn data-active={isAscActive} onClick={onSortAsc}>
            ▲
          </SegBtn>
          <SegBtn data-active={isDescActive} onClick={onSortDesc}>
            ▼
          </SegBtn>
        </Segmented>
      </InputRow>
    </FilterBox>
  );

  return createPortal(node, document.body);
};

export default StringFilter;
